import { createContext, useContext, useEffect, useMemo } from "react";
import { UserLevelInfo, UserInfo, GoodsItem, GoodsGroup } from "../../types";
import {
  getCurrentUserInfo,
  GetUserMembershipLevel,
  BatchQueryGoodsDetail,
  GetMyMemberGoods,
} from "../../api";
import { useQuery } from "@tanstack/react-query";
import { GetUserCredit } from "@/api/common";
import { trackGdp } from "@/utils/gio-report";
import { getReportCompData, getReportVipData, getUserType } from "../../utils";
import { Module, PageDataConfig } from "@/types/config-page";
import { useConfigPage } from "@/hooks/useConfigPage/useConfigPage";
import { parseJson } from "@/pages/par-marketing/configPage/utils";
import * as Sentry from "@sentry/react";
import { sentrySetUser } from "@/utils/sentry";

// 从配置数据中提取商品列表并注册到全局
const extractAndRegisterGoodsList = (configData?: PageDataConfig) => {
  if (!configData?.moduleList?.length) return [];

  const goodsList: { skuId: string; spuId: string; goodsType: number }[] = [];

  // 遍历所有模块
  configData.moduleList.forEach((module: Module) => {
    const moduleJsonData = parseJson(module.moduleJson);
    // 检查模块中是否有商品组数据
    if (moduleJsonData?.goodsInfo?.group?.length) {
      moduleJsonData.goodsInfo.group.forEach((group: GoodsGroup) => {
        if (group?.goodsList?.length) {
          // 提取商品列表中的基本信息
          group.goodsList.forEach((goods: GoodsItem) => {
            if (goods.skuId && goods.spuId && goods.goodsType) {
              goodsList.push({
                skuId: goods.skuId,
                spuId: goods.spuId,
                goodsType: goods.goodsType,
              });
            }
          });
        }
      });
    }
  });

  // 如果有商品数据，注册到全局
  if (goodsList.length > 0) {
    // 对goodsList进行去重
    const uniqueGoodsList = goodsList.filter(
      (item, index, self) =>
        index ===
        self.findIndex(
          (t) =>
            t.spuId === item.spuId &&
            t.skuId === item.skuId &&
            t.goodsType === item.goodsType,
        ),
    );
    return uniqueGoodsList;
  }
  return [];
};

// userContext 上下文定义
type UserContextType = {
  levelInfo: UserLevelInfo | null;
  userInfo: UserInfo | null;
  myMemberGoods: {
    userCouponList: {
      thresholdPrice?: number;
      couponFee?: string;
      couponType?: number;
      couponNum?: number;
      couponStatusName?: string;
    }[];
    userChargeCardList: {
      chargeCardTotalAmt?: string;
      chargeCardValidAmt?: string;
      chargeCardStatus?: string;
    }[];
  } | null; // 会员商品数据
  balanceCredit: number;
  configPageData: PageDataConfig | null; // 配置页面数据
  goodsData: Record<string, GoodsItem> | null; // 商品数据映射，key为skuId
  isLoading: boolean;
  userType: string;
};

export const UserContext = createContext<UserContextType>({
  levelInfo: null,
  userInfo: null,
  myMemberGoods: null,
  balanceCredit: 0,
  configPageData: null,
  goodsData: null,
  isLoading: false,
  userType: "",
});

export const UserProviderWithUserInfo = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // 直接使用导出的独立hooks，避免重复定义
  const { data: userInfo, isLoading: userInfoLoading } = useUserInfoQuery();
  const { data: levelInfo, isLoading: levelInfoLoading } =
    useUserLevelInfoQuery();
  const { data: balanceCredit, isLoading: balanceCreditLoading } =
    useBalanceCreditQuery();

  // 计算整体加载状态
  const pageLoading =
    userInfoLoading || levelInfoLoading || balanceCreditLoading;

  return (
    <UserContext.Provider
      value={{
        levelInfo: levelInfo || null,
        userInfo: userInfo || null,
        balanceCredit: balanceCredit || 0,
        isLoading: pageLoading,
        myMemberGoods: null,
        configPageData: null,
        goodsData: null,
        userType: "",
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserProviderWithConfigPage = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // 获取页面参数
  const { configPageQuery } = useConfigPage();

  // 直接使用导出的独立hooks，避免重复定义
  const { data: userInfo } = useUserInfoQuery();
  const { data: levelInfo, isLoading: levelInfoLoading } =
    useUserLevelInfoQuery();
  const { data: balanceCredit } = useBalanceCreditQuery();
  // 获取配置页面数据
  const { data: configPageData, isLoading: configPageLoading } =
    configPageQuery!;

  const goodsList = useMemo(() => {
    return extractAndRegisterGoodsList(configPageData);
  }, [configPageData]);

  // 统一获取商品数据
  const { data: goodsData, isLoading: goodsDataLoading } =
    useGoodsDataQuery(goodsList);

  const { data: myMemberGoods, isLoading: myMemberGoodsLoading } =
    useMyMemberGoodsQuery();

  const userType = useMemo(() => {
    if (!levelInfo || !myMemberGoods) {
      return "";
    }
    return getUserType(levelInfo, myMemberGoods);
  }, [levelInfo, myMemberGoods]);

  // 计算整体加载状态
  const pageLoading =
    levelInfoLoading ||
    configPageLoading ||
    goodsDataLoading ||
    myMemberGoodsLoading;

  // 数据获取完后，再触发曝光埋点
  useEffect(() => {
    if (
      levelInfo &&
      configPageData &&
      !goodsDataLoading &&
      myMemberGoods
    ) {
      trackGdp("P11222", "B001", {
        ...getReportVipData(levelInfo),
        ...getReportCompData(configPageData),
        activityld: goodsData
          ? Object?.values(goodsData)
              ?.map((item) => item.actCode)
              ?.filter(Boolean)
              ?.join(",")
          : "",
        page_id: configPageData?.pageCode,
        version_id: configPageData?.versionCode,
        userType,
      });
    }
  }, [levelInfo, configPageData, goodsDataLoading, goodsData, myMemberGoods, userType]);

  return (
    <UserContext.Provider
      value={{
        levelInfo: levelInfo || null,
        userInfo: userInfo || null,
        myMemberGoods: myMemberGoods || null,
        balanceCredit: balanceCredit || 0,
        configPageData: configPageData || null,
        goodsData: goodsData || null,
        isLoading: pageLoading,
        userType: userType || "",
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  return useContext(UserContext);
};

// 导出独立的React Query hooks，方便在其他组件中使用
export const useUserInfoQuery = () =>
  useQuery({
    queryKey: ["/web-h5/charge/member/getCurrentUserInfo"],
    queryFn: async () => {
      const res = await getCurrentUserInfo();
      sentrySetUser({
        id: res.data?.uid,
        custom_data: {
          ...(res?.data || {}),
        },
      });
      if (res.code === 200) {
        return res.data;
      }
      Sentry.captureException(new Error("获取用户信息失败"), {
        tags: {
          error_type: "getCurrentUserInfo_no_valid_data",
          biz: "charge-vip",
        },
      });
    },
    refetchOnWindowFocus: true,
  });

export const useUserLevelInfoQuery = () =>
  useQuery({
    queryKey: ["/web-h5/charge/member/getUserMembershipLevel"],
    queryFn: async () => {
      const res = await GetUserMembershipLevel();
      if (res.code === 200) {
        return res.data;
      }
      Sentry.captureException(new Error("获取用户等级信息失败"), {
        tags: {
          error_type: "getUserMembershipLevel_no_valid_data",
          biz: "charge-vip",
        },
      });
    },
    refetchOnWindowFocus: true,
  });

export const useBalanceCreditQuery = () =>
  useQuery({
    queryKey: ["/web-h5/charge/getUserCredit"],
    queryFn: async () => {
      try {
        const res = (await GetUserCredit()) as { balanceCredit?: number };
        return res?.balanceCredit || 0;
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            error_type: "getUserCredit_exception",
            biz: "charge-vip",
          },
        });
        console.log("getUserCredit error -->", error);
        return 0;
      }
    },
    refetchOnWindowFocus: true,
  });

// 统一的商品数据获取hook
export const useGoodsDataQuery = (
  goodsList: { skuId: string; spuId: string; goodsType: number }[],
) => {
  return useQuery({
    queryKey: [
      "goodsData",
      ...goodsList.map((item) => `${item.skuId}-${item.spuId}`),
    ],
    queryFn: async () => {
      const res = await BatchQueryGoodsDetail({
        mallGoodsList: goodsList,
      });

      if ([0, 200].includes(res?.code)) {
        // 将商品数据转换为以skuId为key的映射
        const goodsDataMap: Record<string, GoodsItem> = {};
        res?.data?.mallGoodsInfoList?.forEach((item: GoodsItem) => {
          if (item.skuId) {
            goodsDataMap[item.skuId] = item;
          }
        });
        return goodsDataMap;
      }
      Sentry.captureException(new Error("批量获取商品详情失败"), {
        tags: {
          error_type: "batchQueryGoodsDetail_no_valid_data",
          biz: "charge-vip",
        },
      });
      return {};
    },
    enabled: goodsList.length > 0, // 只有当有商品列表时才启用
    refetchOnWindowFocus: true,
  });
};

const useMyMemberGoodsQuery = () => {
  return useQuery({
    queryKey: ["/web-h5/charge/member/queryMyMemberGoods"],
    queryFn: async () => {
      const res = await GetMyMemberGoods();
      if (res.code === 200) {
        return res.data;
      }
      Sentry.captureException(new Error("获取我的会员商品失败"), {
        tags: {
          error_type: "getMyMemberGoods_no_valid_data",
          biz: "charge-vip",
        },
      });
      return {};
    },
    refetchOnWindowFocus: true,
  });
};
